import { createRef, MutableRefObject } from "react";
import { createStore } from "zustand";
import { RagDao } from "../graphql/ragDao";
import {
  ChatReply,
  Citation,
  DuplicateResult,
  ExchangeFeedback,
  ExchangeFeedbackInput,
  Guideline,
  PredefinedInsight,
  QuestionOptions,
  SessionExchange,
  SessionItem,
} from "../graphql/types/graphql";
import { AlertColor } from "@mui/material";
import {
  ANONYMOUS_SESSION_KEY,
  CONFIRM_PROFESSIONAL_KEY,
  USER_ROLE_KEY,
  ASSESSMENT_EXCHANGE_INDEX,
  PLAN_EXCHANGE_INDEX,
} from "../constants";
import { CitationMap, FootnotesMap } from "fe-web-components/references/types";
import { NO_GUIDELINE } from "fe-web-components/guidelines/types";
import { FeedbackInput } from "fe-web-components/feedback/types";
import { rearrangeCitations } from "fe-web-components/references/utils";
import { TranslateByGender } from "fe-common/locale/useTranslateByGender";
import { RagGuideline } from "fe-common/graphql";

export enum MessageType {
  question = 1,
  answer = 2,
  suggestedQuestions = 3,
}

export enum SessionActionStatus {
  invalid,
  error,
  success,
}

export enum LoginRequestType {
  none,
  login,
  saveChat,
}

export enum SessionStatus {
  normal,
  updating,
  deleting,
  duplicating,
}

export enum QuestionTypes {
  Chat = "Chat",
}
type QuestionType = QuestionTypes | PredefinedInsight | string;

export type SessionWithStatus = SessionItem & { status: SessionStatus };

export interface ChatMessage {
  index: number;
  type: MessageType;
  text: string;
  citations?: CitationMap;
  guidelines?: Guideline[];
  footNotes?: FootnotesMap; //map of ruleValue index to citation index
  questions?: string[]; //suggested questions
  feedback?: ExchangeFeedback;
  ref?: MutableRefObject<any>; //save the ref of the message for scrolling
}

export interface RagSessionSettings {
  modelVer: string;
  debugMode: boolean;
  promptVariants: string[];
  variant: string;
  wssEndpoint: string;
  rtlDisplay?: boolean;
  embeddedView?: boolean;
}

export interface User {
  userName: string;
  allowDebug: boolean;
}

export type GuidelineData = {
  guideline: RagGuideline;
  data: ChatMessage;
};

interface SessionState {
  loadingRagSettings: boolean;
  ragSettings: RagSessionSettings;
  loadingReplies: string[];
  loadingChatSession: boolean;
  loadingSessionsList: boolean;
  sessionsList: SessionWithStatus[];
  sessionId: string;
  patientSummary: { text: string; valid: boolean } | null;
  wsSessionId: string;
  chatMessages: ChatMessage[];
  chatHistory: ChatMessage[];
  chatContext: any;
  chatActive: boolean;
  debugInfo: Record<string, any> | null;
  streamId: string;
  mainMessage: { severity: AlertColor; text: string };
  sessionUser: User;
  loginRequest: LoginRequestType;
  confirmRequired: boolean;
  withSuggestions: boolean;
  autoScroll: boolean;
}

export interface SessionStore extends SessionState {
  initClient: () => void;
  fetchSessionsList: (showOnlyActive: boolean, username: string) => void;
  fetchRagSettings: (
    initialSettings?: Partial<RagSessionSettings>,
  ) => Promise<RagSessionSettings>;
  setRagSettings: (newSettings: Partial<RagSessionSettings>) => void;
  startSession: (sessionId: string, loginRequired: boolean) => void;
  addSession: (
    summary: string,
    updatedFromId?: string,
  ) => Promise<SessionActionStatus>;
  removeSession: (
    sessionId: string,
    changeStatus: boolean,
  ) => Promise<SessionActionStatus>;
  duplicateSession: (
    sessionId: string,
    takeover: boolean,
  ) => Promise<DuplicateResult>;
  resetSession: () => void;
  addQuestion: (msg: string, onReply?: (answer: ChatReply) => void) => void;
  replyQuestion: (
    msg: string,
    index?: number,
    options?: QuestionOptions,
  ) => Promise<ChatReply>;
  replaceQuestion: (
    index: number,
    msg: string,
    options?: QuestionOptions,
    onReply?: (answer: ChatReply) => void,
  ) => void;
  loadGuideline: (
    exchangeIndex: number,
    guidelineId: string,
  ) => Promise<ChatMessage>;
  setStreamId: (streamId: string) => void;
  sendFeedback: (
    feedback: FeedbackInput,
    exchangeIndex?: number,
    promptName?: string,
    predefinedInsight?: PredefinedInsight,
  ) => Promise<boolean>;
  setMainMessage: (message: { severity: AlertColor; text: string }) => void;
  setSessionUser: (user: User) => void;
  setLoginRequest: (requestType: LoginRequestType) => void;
  setConfirmRequired: (required: boolean, userRole: string) => void;
  userTakeover: () => Promise<string>;
  setAutoScroll: (autoScroll: boolean) => void;
  setAppHeader: (h: string) => any;
  startSessionByCaseId: (
    patientCase: string,
    modelver: string,
    generateSuggestion: boolean,
    caseId: string,
  ) => Promise<SessionActionStatus>;
  askPromptInSession: (
    options: QuestionOptions,
    predefinedInsight?: PredefinedInsight,
    prompt?: string,
    promptName?: string,
    useGuide?: string,
  ) => Promise<ChatMessage>;
  replyIsLoading: (questionType: QuestionType) => boolean;
}

type SetState = (
  partial:
    | SessionStore
    | Partial<SessionStore>
    | ((state: SessionStore) => SessionStore | Partial<SessionStore>),
  replace?: boolean | undefined,
) => void;
type GetState = () => SessionStore;

const DEFAULT_SESSION_SETTINGS: RagSessionSettings = {
  modelVer: "3.5",
  debugMode: false,
  variant: null,
  promptVariants: null,
  wssEndpoint: null,
};
const DEFAULT_WELCOME_MESSAGE =
  "Welcome to Kahun Dialogue!\n" +
  "You can ask us any clinical question about this patient.";
function removeAssessmentPlanExchanges(
  exchanges: SessionExchange[],
): SessionExchange[] {
  return exchanges.filter(
    (exchange) =>
      exchange.index !== ASSESSMENT_EXCHANGE_INDEX &&
      exchange.index !== PLAN_EXCHANGE_INDEX,
  ); //TODO: move this to server
}
function setQuestionIsLoading(
  set: SetState,
  get: GetState,
  questionType: QuestionType,
) {
  set({ loadingReplies: [...get().loadingReplies, questionType] });
}

function setQuestionFinishedLoading(
  set: SetState,
  get: GetState,
  questionType: QuestionType,
) {
  set({
    loadingReplies: get().loadingReplies.filter((rid) => rid !== questionType),
  });
}

function formatAnswer(
  index: number,
  text: string,
  citations?: Citation[],
  feedback?: ExchangeFeedback,
  guidelines?: Guideline[],
  altGuidelines?: Guideline[],
) {
  const rearrangedCitations = rearrangeCitations(
    citations.map((c) => ({
      ruleCitations: c.ruleCitations,
      rvids: c.rvids,
      text: c.text,
    })),
  );
  return {
    index,
    type: MessageType.answer,
    text,
    citations: rearrangedCitations?.citationsMap,
    footNotes: rearrangedCitations?.footnotesMap,
    feedback,
    guidelines: guidelines?.concat(altGuidelines || []),
  };
}

const addAnswer = (set: SetState, get: GetState) => (reply: ChatReply) => {
  const { text, index, citations, guidelines, altGuidelines } = reply;

  set({
    chatMessages: [
      ...get().chatMessages,
      {
        ...formatAnswer(
          parseInt(index),
          text,
          citations,
          null,
          guidelines,
          altGuidelines,
        ),
      },
    ],
  });
};

const setSessionStatus =
  (set: SetState) => (sessionId: string, status: SessionStatus) => {
    set((prevState) => ({
      sessionsList: prevState.sessionsList.map((s) =>
        s.sessionId === sessionId ? { ...s, status } : s,
      ),
    }));
  };

function resetSession(set: SetState) {
  clearAnonymousSessionId();
  set({
    patientSummary: null,
    chatMessages: [],
    chatContext: null,
    chatActive: false,
    sessionId: null,
    debugInfo: null,
    loadingReplies: [],
  });
}

function processExchanges(exchanges: SessionExchange[] = []) {
  return removeAssessmentPlanExchanges(exchanges)
    .map((exchange) => {
      const sessionMessage = [];

      if (exchange.question) {
        sessionMessage.push({
          index: exchange.index,
          type: MessageType.question,
          text: exchange.question,
        });
      }
      if (exchange.answer) {
        sessionMessage.push(
          formatAnswer(
            exchange.index,
            exchange.answer,
            exchange.citations,
            exchange.feedback,
            exchange.guidelines,
            exchange.altGuidelines,
          ),
        );
      }

      return sessionMessage;
    })
    .flat();
}

function clearAnonymousSessionId() {
  sessionStorage.removeItem(ANONYMOUS_SESSION_KEY);
}

function saveAnonymousSessionId(sessionId: string) {
  sessionStorage.setItem(ANONYMOUS_SESSION_KEY, sessionId);
}
export const createSessionStore = (
  translate?: TranslateByGender,
  initialState?: Partial<SessionStore>,
) => {
  let RAG_DAO = new RagDao();
  const handleError = (set: SetState) => (error: any) => {
    console.error(error);
    set({
      mainMessage: {
        severity: "error",
        text:
          typeof error === "string"
            ? error
            : translate
              ? translate("DIALOGUE_GENERAL_ERROR")
              : "Communication Error",
      },
    });
  };

  function insertSuggestedQuestion(
    questions: string[],
    embeddedView: boolean,
  ): ChatMessage {
    return {
      index: -1,
      type: MessageType.suggestedQuestions,
      text: translate
        ? translate(
            !embeddedView
              ? "DIALOGUE_WELCOME_MESSAGE"
              : "DIALOGUE_WELCOME_MESSAGE_EMBEDDED",
          )
        : DEFAULT_WELCOME_MESSAGE,
      questions,
      ref: createRef(),
    };
  }

  function sessionMessages(
    exchanges: SessionExchange[],
    suggestedQuestions: string[],
    embeddedView: boolean,
  ): ChatMessage[] {
    return [
      insertSuggestedQuestion(suggestedQuestions, embeddedView),
      ...processExchanges(exchanges),
    ];
  }

  return createStore<SessionStore>()((set, get) => ({
    ...initialState,
    initClient: () => {
      RAG_DAO = new RagDao(); //make sure we have a new graphQL client with the latest token when user logs out so new sessions will be anonymous
      resetSession(set);
      set({ ragSettings: null });
    },
    sessionId: null,
    ragSettings: initialState?.ragSettings,
    loadingRagSettings: true,
    fetchRagSettings: async (initialSettings) => {
      set({ loadingRagSettings: true });
      try {
        const result = await RAG_DAO.geInitialInfo();
        const sessionInfo = {
          ...DEFAULT_SESSION_SETTINGS,
          ...initialSettings,
          promptVariants: result.promptVariants,
          wssEndpoint: result.wssEndpoint,
          variant: result.promptVariants[0],
        };
        get().setRagSettings(sessionInfo);
        return sessionInfo;
      } catch (e) {
        console.error(e);
        handleError(set)("Oops! Something went wrong. Please try again.");
        return DEFAULT_SESSION_SETTINGS;
      } finally {
        set({ loadingRagSettings: false });
      }
    },
    setAppHeader: (app: string) => {
      RAG_DAO?.setAppHeader(app);
    },
    setRagSettings: (newSettings: Partial<RagSessionSettings>) =>
      set({ ragSettings: { ...get().ragSettings, ...newSettings } }),
    patientSummary: null,
    chatMessages: [],
    chatHistory: null,
    chatContext: null,
    chatActive: false,
    debugInfo: null,
    loadingReplies: [],
    loadingChatSession: true,
    loadingSessionsList: false,
    sessionsList: [],
    fetchSessionsList: async (
      showOnlyActive: boolean = true,
      username = null,
    ) => {
      set({ loadingSessionsList: true });
      try {
        const result = username
          ? await RAG_DAO.getSessionsByUser(showOnlyActive, username)
          : await RAG_DAO.getSessions(showOnlyActive);
        if (result) {
          set({
            sessionsList: result
              .sort((a, b) => Date.parse(b.date) - Date.parse(a.date))
              .map((s) => ({
                ...s,
                date: s.date ? new Date(s.date).toLocaleString() : null,
                status: SessionStatus.normal,
              })),
          });
        }
      } catch (e) {
        handleError(set)(e);
      } finally {
        set({ loadingSessionsList: false });
      }
      return;
    },
    streamId: null,
    setStreamId: (streamId: string) => set({ streamId }),
    wsSessionId: null,
    startSession: async (sessionId: string, loginRequired: boolean) => {
      const { patientSummary, sessionUser } = get();
      if (loginRequired && patientSummary && !sessionUser?.userName) {
        set({ loginRequest: LoginRequestType.saveChat });
        return;
      }
      if (sessionId === "new") {
        resetSession(set);
      } else if (sessionUser) {
        set({ loadingChatSession: true });
        await RAG_DAO.restoreSession(sessionId, sessionUser.allowDebug)
          ?.then((result) => {
            if (!result) {
              handleError(set)("Could not restore session");
            } else {
              set({
                patientSummary: { text: result.patientCase, valid: true },
                wsSessionId: result.wsSessionId,
              });

              set({
                chatMessages: sessionMessages(
                  result.exchanges,
                  result.suggestedQuestions,
                  get().ragSettings.embeddedView,
                ),
                chatContext: result.context,
                chatActive: true,
              });
            }
          })
          .catch((e) => {
            handleError(set)(e);
          })
          .finally(() => {
            set({ sessionId, loadingChatSession: false });
          });
      }
    },
    startSessionByCaseId: async (
      patientCase: string,
      modelver: string,
      generateSuggestion: boolean,
      caseId: string,
    ) => {
      set({ loadingChatSession: true });
      const { sessionUser } = get();
      try {
        const session = await RAG_DAO.startSessionByCaseId(
          sessionUser?.allowDebug || false,
          patientCase,
          modelver,
          generateSuggestion,
          caseId,
        );
        if (!session) {
          handleError(set)("Could not start chat");
          return SessionActionStatus.error;
        }

        set({
          patientSummary: { text: patientCase, valid: session.valid },
          wsSessionId: session.session.wsSessionId,
          chatContext: session.session.context,
          chatMessages: sessionMessages(
            session.session.exchanges,
            session.session.suggestedQuestions,
            get().ragSettings.embeddedView,
          ),
          chatHistory: session.history?.length && [
            ...processExchanges(session.history),
          ],
          chatActive: true,
          loadingChatSession: false,
          sessionId: session.sessionId,
        });
        if (!session.valid) {
          return SessionActionStatus.invalid;
        }
        return SessionActionStatus.success;
      } catch (e) {
        handleError(set)(e);
        set({ loadingChatSession: false });
        return SessionActionStatus.error;
      }
    },
    addSession: async (
      summary: string,
      updatedFromId?: string,
    ): Promise<SessionActionStatus> => {
      set({ loadingChatSession: true });
      const { ragSettings, sessionUser } = get();
      if (updatedFromId)
        setSessionStatus(set)(updatedFromId, SessionStatus.updating);
      let success = false;
      try {
        const generateSuggestions =
          sessionUser?.allowDebug || Math.random() < 0.5; //AB testing for suggested questions;
        set({ withSuggestions: generateSuggestions });
        const result = await RAG_DAO.startSession(
          summary,
          ragSettings.modelVer,
          generateSuggestions,
        );
        if (result) {
          set({
            patientSummary: { text: summary, valid: result.reply.valid },
          });
          if (!result.reply.valid) return SessionActionStatus.invalid; //invalid summary
          if (updatedFromId) {
            //delete the old session
            await get().removeSession(updatedFromId, false);
          }
          if (!get().sessionUser?.userName) {
            saveAnonymousSessionId(result.sessionId);
          }
          set({
            sessionId: result.sessionId,
            wsSessionId: result.reply?.wsSessionId,
            chatContext: result.reply.context,
            sessionsList: [
              ...get().sessionsList,
              {
                title: result.reply.title,
                sessionId: result.sessionId,
                status: SessionStatus.normal,
              },
            ],
            chatMessages: [
              insertSuggestedQuestion(
                generateSuggestions ? result.reply.sampleQuestions : null,
                get().ragSettings.embeddedView,
              ),
            ], //first message can include suggested questions
            chatActive: true,
          });
          success = true;
          return SessionActionStatus.success; //valid summary
        } else {
          handleError(set)("Could not start chat");
          return SessionActionStatus.error; //could not start session
        }
      } catch (e) {
        handleError(set)(e);
      } finally {
        if (updatedFromId && !success)
          setSessionStatus(set)(updatedFromId, SessionStatus.normal);
        set({ loadingChatSession: false });
      }
    },
    removeSession: async (sId: string, changeStatus: boolean) => {
      //changeStatus indicates weather the session in being deleted and not updated
      let success = false;
      if (changeStatus) setSessionStatus(set)(sId, SessionStatus.deleting); //show the deleting icon
      try {
        const result = await RAG_DAO.removeSession(sId);
        if (result?.status === "success") {
          set({
            sessionsList: get().sessionsList.filter(
              (session) => session.sessionId !== sId,
            ),
          });
          success = true;
          if (changeStatus) resetSession(set);
          return SessionActionStatus.success;
        } else {
          handleError(set)(
            `Could not delete ${
              changeStatus ? "dialogue" : "previous version"
            }`,
          );
          return SessionActionStatus.error;
        }
      } catch (e) {
        handleError(set)(e);
        return SessionActionStatus.error;
      } finally {
        if (!success) setSessionStatus(set)(sId, SessionStatus.normal);
      }
    },
    duplicateSession: async (sId: string, takeover: boolean) => {
      setSessionStatus(set)(sId, SessionStatus.duplicating); //show the duplicating icon on session list
      return await RAG_DAO.duplicateSession(sId, takeover)
        .catch((e) => {
          handleError(set)(e);
          return null;
        })
        .finally(() => {
          setSessionStatus(set)(sId, SessionStatus.normal);
        });
    },
    replyQuestion: async (
      text: string,
      index = -1,
      options: QuestionOptions = {},
    ) => {
      const { chatContext, ragSettings, sessionId, streamId } = get();
      const questionId = QuestionTypes.Chat;
      setQuestionIsLoading(set, get, questionId);
      try {
        const result = await RAG_DAO.answerQuestionInSession(
          chatContext,
          text,
          {
            variant: ragSettings.variant,
            streamId,
            questionId,
            index,
            ...options,
          },
          sessionId,
        );
        if (result) {
          addAnswer(set, get)(result);
          set({ chatContext: result.context, debugInfo: result.debug });
          return result;
        } else {
          handleError(set)("Could not get answer");
          return null;
        }
      } catch (e) {
        handleError(set)(e);
        return null;
      } finally {
        setQuestionFinishedLoading(set, get, questionId);
      }
    },
    loadGuideline: async (
      exchangeIndex,
      guidelineId: string,
    ): Promise<ChatMessage> => {
      //update guideline text inside the chat
      const { chatContext, chatMessages, ragSettings, streamId } = get();
      const questionType = QuestionTypes.Chat;
      //setQuestionIsLoading(set, get, questionType);
      const questionText = chatMessages.find(
        (msg) => msg.index === exchangeIndex,
      )?.text;
      const options: QuestionOptions = {
        guidelineId: guidelineId === NO_GUIDELINE ? null : guidelineId,
        forceGuidelines: guidelineId !== NO_GUIDELINE,
        disableGuidelines: guidelineId === NO_GUIDELINE,
      };
      try {
        const result = await RAG_DAO.answerQuestion(chatContext, questionText, {
          variant: ragSettings.variant,
          streamId,
          questionId: questionType,
          index: exchangeIndex,
          ...options,
        });
        if (result) {
          set({
            debugInfo: result.debug,
          });
          return formatAnswer(exchangeIndex, result.text, result.citations);
        } else {
          handleError(set)(
            `Could not load ${
              guidelineId !== NO_GUIDELINE
                ? "guideline"
                : "answer from knowledge base"
            }`,
          );
          return null;
        }
      } catch (e) {
        handleError(set)(e);
        return null;
      } finally {
        //setQuestionFinishedLoading(set, get, questionType);
      }
    },
    resetSession: () => {
      resetSession(set);
    },
    addQuestion: (text: string, onReply?: (answer: ChatReply) => void) => {
      const prevMessages = get().chatMessages;
      set({
        chatMessages: [
          ...prevMessages,
          {
            index: prevMessages.length
              ? prevMessages[prevMessages.length - 1].index + 1
              : 0,
            type: MessageType.question,
            text,
          },
        ],
        autoScroll: true, //enable autoscroll when user sends a message
      });
      get()
        .replyQuestion(text)
        .then((answer) => {
          if (onReply) onReply(answer);
        });
    },
    replaceQuestion: async (
      exchangeIndex: number,
      text: string,
      options?: QuestionOptions,
    ) => {
      set({ autoScroll: true }); //re-enable autoscroll when user edits a message
      // @ts-ignore
      const msgIndex =
        get().chatMessages.findLastIndex(
          (msg: ChatMessage) => msg.index === exchangeIndex,
        ) - 1;
      get()
        .replyQuestion(text, exchangeIndex, options || {})
        .catch((e) => {
          console.error("could not undo question", exchangeIndex);
          handleError(set)(e);
        });

      const prevMessages = get().chatMessages;
      set({
        chatMessages: [
          ...prevMessages.slice(0, msgIndex),
          {
            index: exchangeIndex,
            type: MessageType.question,
            text,
          },
        ],
      });
    },
    sendFeedback: async (
      feedback: ExchangeFeedbackInput,
      exchangeIndex?: number,
      promptName?: string,
      predefinedInsight?: PredefinedInsight,
    ): Promise<boolean> => {
      try {
        const sessionId = get().sessionId;
        let reply: ExchangeFeedback;
        if (promptName || predefinedInsight)
          reply = await RAG_DAO.updatePromptFeedback(
            sessionId,
            promptName,
            predefinedInsight,
            feedback,
          );
        else
          reply = await RAG_DAO.updateExchangeFeedback(
            sessionId,
            exchangeIndex,
            feedback,
          );

        if (!reply) {
          handleError(set)("Could not send feedback");
          return false;
        } else {
          set({
            mainMessage: {
              severity: "success",
              text: "Thank you for your feedback!",
            },
          });
          return true;
        }
      } catch (e) {
        handleError(set)(e);
        return false;
      }
    },
    mainMessage: null,
    setMainMessage: (message: { severity: AlertColor; text: string }) =>
      set({ mainMessage: message }),
    sessionUser: null,
    setSessionUser: (user: User) => set({ sessionUser: user }),
    loginRequest: null,
    setLoginRequest: (requestType: LoginRequestType) =>
      set({ loginRequest: requestType }),
    confirmRequired: localStorage.getItem(CONFIRM_PROFESSIONAL_KEY) !== "true",
    setConfirmRequired: (required: boolean, userRole: string) => {
      if (!required) {
        localStorage.setItem(CONFIRM_PROFESSIONAL_KEY, "true");
        localStorage.setItem(USER_ROLE_KEY, userRole);
      } else {
        localStorage.removeItem(CONFIRM_PROFESSIONAL_KEY);
        localStorage.removeItem(USER_ROLE_KEY);
      }
      set({
        confirmRequired: required,
      });
    },
    userTakeover: async () => {
      const anonymousSessionId = sessionStorage.getItem(ANONYMOUS_SESSION_KEY);
      if (anonymousSessionId) {
        try {
          const result = await RAG_DAO.userTakeover(anonymousSessionId);
          if (result.status === "success") {
            clearAnonymousSessionId();
            return anonymousSessionId;
          } else {
            handleError(set)("Could not save anonymous session");
            return null;
          }
        } catch (e) {
          handleError(set)("Could not save anonymous session");
          return null;
        }
      }
      return null;
    },
    withSuggestions: undefined,
    autoScroll: true,
    setAutoScroll: (autoScroll: boolean) => set({ autoScroll }),
    askPromptInSession: async (
      options: QuestionOptions,
      predefinedInsight?: PredefinedInsight,
      prompt?: string,
      promptName?: string,
      useGuide?: string,
    ) => {
      const { chatContext, ragSettings, streamId } = get();
      const questionType = predefinedInsight || promptName;
      const sessionId = get().sessionId;
      setQuestionIsLoading(set, get, questionType);
      try {
        const answer = await RAG_DAO.askPromptInSession(
          chatContext,
          {
            variant: ragSettings.variant,
            streamId,
            questionId: questionType,
            guidelineId: useGuide || options.guidelineId,
            forceGuidelines: !!useGuide || options.forceGuidelines,
            disableGuidelines: options.disableGuidelines,
          },
          predefinedInsight,
          prompt,
          promptName,
          sessionId,
        );
        if (!answer) return null;
        return formatAnswer(
          0,
          answer.text,
          answer.citations,
          null,
          answer.guidelines,
          answer.altGuidelines,
        );
      } catch (e) {
        handleError(set)(e);
        return null;
      } finally {
        setQuestionFinishedLoading(set, get, questionType);
      }
    },
    replyIsLoading: (questionType) => {
      return get().loadingReplies.includes(questionType);
    },
  }));
};
